import type { WidaStudent, WidaYearRecord } from '../Upload/widaHistory';
import { assessmentScaleOf, latestAdministrationYear } from '../Upload/widaHistory';
import type { SisStudent } from '../Upload/sisRoster';
import { findSisInRoster, parseTeachers, schoolYearEndFromDate } from '../Upload/sisRoster';
import { classifyElStatus, loadThresholds, type Thresholds } from '../../core/thresholds';
import { deriveEnrollment, type EnrollmentStatus } from './enrollment';
import { shownElStatus, type RosterClassificationRow } from './rosterClassification';
import { schoolYearEndForDate } from '../../core/schoolYear';

export type RosterElStatus = 'active' | 'monitoring' | 'former' | 'waived';

export type RosterCohort = 'ltel' | 'newcomer' | 'dually_identified';

export type PlanSignOffValue = 'not_complete' | 'complete' | 'not_finalized';

export type PlanSignOff = { value: PlanSignOffValue; signed: number; required: number };

export function planSignOffText(status: PlanSignOff | null | undefined): string {
  if (!status) return '—';
  if (status.value === 'not_finalized') return 'Not finalized';
  if (status.required === 0) return 'No reviewers yet';
  return `${status.signed} of ${status.required} signed off`;
}

export const COHORT_ORDER: readonly RosterCohort[] = ['ltel', 'newcomer', 'dually_identified'];

export type RosterRow = {
  id: string;
  name: string;
  school: string;
  teachers: string[];
  grade: string;
  elStatus: RosterElStatus;
  cohorts: RosterCohort[];
  yearsInEl: number | null;
  compositePL: number | null;
  domainPLs: Record<RosterDomainKey, number | null>;
  missingDomains: RosterDomainKey[];
  enrollment: EnrollmentStatus;
  sortKey: string;
  planSignOff?: PlanSignOff | null;
  // True when the SIS status column is blank and this row's 'active' is a
  // fallback rather than anything the district wrote down. A value this app
  // cannot parse is still stated. Absent means stated.
  statusInferred?: boolean;
  // True when no WIDA file scored this student, so the plan route has nothing
  // to open for them. Absent means the student's action plan can be opened.
  noPlan?: boolean;
  heldOut?: boolean;
  source?: string;
};

export type RosterDomainKey = 'listening' | 'speaking' | 'reading' | 'writing';

export const DOMAIN_KEYS: readonly RosterDomainKey[] = [
  'listening',
  'speaking',
  'reading',
  'writing',
];

export const DOMAIN_LABELS: Record<RosterDomainKey, string> = {
  listening: 'Listening',
  speaking: 'Speaking',
  reading: 'Reading',
  writing: 'Writing',
};

const EM_DASH = '—';

export function formatGradeOrdinal(grade: string): string {
  const trimmed = grade.trim();
  if (trimmed === '') return EM_DASH;
  const lower = trimmed.toLowerCase();
  if (lower === 'k' || lower === 'kg' || lower === 'kindergarten') return 'K';
  if (!/^\d+$/.test(trimmed)) return trimmed;
  const n = parseInt(trimmed, 10);
  if (n === 0) return 'K';
  const tens = n % 100;
  const suffix =
    tens >= 11 && tens <= 13
      ? 'th'
      : n % 10 === 1
        ? 'st'
        : n % 10 === 2
          ? 'nd'
          : n % 10 === 3
            ? 'rd'
            : 'th';
  return `${n}${suffix}`;
}

function displayName(wida: WidaStudent | null, fallbackId: string): {
  name: string;
  sortKey: string;
} {
  const last = wida?.lastName.trim() ?? '';
  const first = wida?.firstName.trim() ?? '';
  if (last === '' && first === '') {
    const name = `Student ${fallbackId}`;
    return { name, sortKey: name.toLowerCase() };
  }
  const name = last === '' ? first : first === '' ? last : `${last}, ${first}`;
  return { name, sortKey: `${last} ${first}`.trim().toLowerCase() };
}

type ElStatusBucket = 'active' | 'monitoring' | 'reclassified' | 'inactive' | 'unknown';

function elStatusBucket(sis: SisStudent | null): ElStatusBucket {
  const text = sis?.elStatus?.toLowerCase().trim() ?? '';
  if (text.includes('monitor')) return 'monitoring';
  if (text.includes('reclass')) return 'reclassified';
  // Kentucky's SIS records a former EL as "EL Exited". Tested before the
  // active forms so "exited" is never read as "active".
  if (text.includes('exit')) return 'reclassified';
  if (/inactive|not active|non-active|non active/.test(text)) return 'inactive';
  if (text.includes('active')) return 'active';
  return 'unknown';
}

function deriveElStatus(bucket: ElStatusBucket): RosterElStatus {
  if (bucket === 'monitoring') return 'monitoring';
  if (bucket === 'reclassified' || bucket === 'inactive') return 'former';
  return 'active';
}

function exitedThisYear(sis: SisStudent | null, currentYear: number): boolean {
  if ((sis?.exitReason ?? '').trim() === '') return false;
  const exitYearEnd = schoolYearEndFromDate(sis?.exitDate ?? '');
  return exitYearEnd == null || exitYearEnd === currentYear;
}

function inProgramNow(
  bucket: ElStatusBucket,
  sis: SisStudent | null,
  currentYear: number,
): boolean {
  if (bucket === 'active') return true;
  if (bucket !== 'unknown') return false;
  return !exitedThisYear(sis, currentYear);
}

function tenureYears(sis: SisStudent | null, reportYear: number): number | null {
  const explicit = sis?.yearsInEl ?? null;
  if (explicit != null) return explicit === 0 ? 1 : explicit;
  if (sis?.serviceStartYear != null) {
    const derived = reportYear - sis.serviceStartYear + 1;
    return derived >= 1 ? derived : null;
  }
  return null;
}

function deriveCohorts(
  sis: SisStudent | null,
  bucket: ElStatusBucket,
  thresholds: Thresholds,
  reportYear: number,
): RosterCohort[] {
  if (!inProgramNow(bucket, sis, reportYear)) return [];
  const classified = classifyElStatus(tenureYears(sis, reportYear), thresholds);
  const found = new Set<RosterCohort>();
  if (classified === 'ltel') found.add('ltel');
  if (sis?.newcomerFlag === true || (classified === 'newcomer' && sis?.newcomerFlag !== false)) {
    found.add('newcomer');
  }
  if (sis?.duallyIdentified === true) found.add('dually_identified');
  return COHORT_ORDER.filter((cohort) => found.has(cohort));
}

function latestAdministration(wida: WidaStudent | null): WidaYearRecord | null {
  let latest: WidaYearRecord | null = null;
  for (const year of wida?.years ?? []) {
    if (year.waived || assessmentScaleOf(year) !== 'access') continue;
    if (latest == null || year.year > latest.year) latest = year;
  }
  return latest;
}

function domainLevels(record: WidaYearRecord | null): Record<RosterDomainKey, number | null> {
  return {
    listening: record?.listening.level ?? null,
    speaking: record?.speaking.level ?? null,
    reading: record?.reading.level ?? null,
    writing: record?.writing.level ?? null,
  };
}

function toRow(
  wida: WidaStudent | null,
  sis: SisStudent | null,
  thresholds: Thresholds,
  reportYear: number,
  enrollment: EnrollmentStatus,
): RosterRow {
  const id = wida?.id || sis?.studentId || '';
  const { name, sortKey } = displayName(wida, id);
  const bucket = elStatusBucket(sis);
  const elStatus = deriveElStatus(bucket);
  const cohorts = deriveCohorts(sis, bucket, thresholds, reportYear);
  const latest = latestAdministration(wida);
  const domainPLs = domainLevels(latest);
  return {
    id,
    name,
    sortKey,
    school: sis?.school || wida?.latestSchool || EM_DASH,
    teachers: parseTeachers(sis?.elTeacher ?? null),
    grade: formatGradeOrdinal(sis?.grade || wida?.latestGrade || ''),
    elStatus,
    cohorts,
    yearsInEl: tenureYears(sis, reportYear),
    compositePL: latest?.composite.level ?? null,
    domainPLs,
    missingDomains: DOMAIN_KEYS.filter((key) => domainPLs[key] == null),
    enrollment,
    statusInferred: (sis?.elStatus ?? '').trim() === '',
    ...(wida == null ? { noPlan: true } : {}),
  };
}

function serverName(row: RosterClassificationRow, id: string): { name: string; sortKey: string } {
  const last = (row.lastName ?? '').trim();
  const first = (row.firstName ?? '').trim();
  if (last === '' && first === '') {
    const name = `Student ${id}`;
    return { name, sortKey: name.toLowerCase() };
  }
  const name = last === '' ? first : first === '' ? last : `${last}, ${first}`;
  return { name, sortKey: `${last} ${first}`.trim().toLowerCase() };
}

function serverElStatus(row: RosterClassificationRow): RosterElStatus {
  const shown = shownElStatus(row);
  if (shown === 'monitoring') return 'monitoring';
  if (shown === 'waived') return 'waived';
  if (shown === 'reclassified' || shown === 'inactive' || shown === 'former') {
    return 'former';
  }
  if (!row.inProgram && row.statusStated !== true) return 'former';
  return 'active';
}

function serverCohorts(row: RosterClassificationRow): RosterCohort[] {
  if (!row.inProgram) return [];
  const held: Record<RosterCohort, boolean> = {
    ltel: row.isLtel,
    newcomer: row.isNewcomer,
    dually_identified: row.duallyIdentified,
  };
  return COHORT_ORDER.filter((cohort) => held[cohort]);
}

function serverDomainLevels(
  levels: Record<string, number> | undefined,
): Record<RosterDomainKey, number | null> {
  return {
    listening: levels?.listening ?? null,
    speaking: levels?.speaking ?? null,
    reading: levels?.reading ?? null,
    writing: levels?.writing ?? null,
  };
}

// The server assembles the same uploads the report is generated from, so a row
// here and a row in the report cannot disagree about a student.
export function classificationToRosterRow(row: RosterClassificationRow): RosterRow {
  const widaKey = (row.widaKey ?? '').trim();
  const id = widaKey || row.studentKey;
  const { name, sortKey } = serverName(row, id);
  const onAccess = row.assessmentScale !== 'alternate_access';
  const domainPLs = serverDomainLevels(onAccess ? row.domainProficiency : undefined);
  return {
    id,
    name,
    sortKey,
    school: (row.school ?? '').trim() || EM_DASH,
    teachers: (row.elTeachers ?? []).map((teacher) => teacher.trim()).filter(Boolean),
    grade: formatGradeOrdinal(row.grade ?? ''),
    elStatus: serverElStatus(row),
    cohorts: serverCohorts(row),
    yearsInEl: row.yearsInProgram > 0 ? row.yearsInProgram : null,
    compositePL: onAccess ? (row.compositeProficiency ?? null) : null,
    domainPLs,
    missingDomains: DOMAIN_KEYS.filter((key) => domainPLs[key] == null),
    enrollment: row.enrollment === 'departed' ? 'departed' : 'active',
    statusInferred: row.statusStated !== true,
    ...(widaKey === '' ? { noPlan: true } : {}),
    ...(row.heldOut === true ? { heldOut: true } : {}),
    ...(row.rosterSource ? { source: row.rosterSource } : {}),
  };
}

export function flashlightOnly(row: RosterRow, accessUploaded: boolean): boolean {
  return accessUploaded && row.noPlan === true && row.source === 'flashlight360';
}

export function buildRosterRows(
  wida: WidaStudent[],
  sis: SisStudent[],
  thresholds: Thresholds = loadThresholds(),
  now: Date = new Date(),
  prior: SisStudent[] = [],
): RosterRow[] {
  const reportYear = latestAdministrationYear(wida) ?? schoolYearEndForDate(now);
  const enrolled = deriveEnrollment(sis, prior);
  const roster = enrolled.map((entry) => entry.student);
  const enrollmentOf = new Map(enrolled.map((entry) => [entry.student, entry.enrollment]));
  const statusFor = (student: SisStudent | null): EnrollmentStatus =>
    (student == null ? undefined : enrollmentOf.get(student)) ?? 'active';

  const matchedSis = new Set<SisStudent>();
  const rows = wida.map((student) => {
    const candidateIds = [student.id, student.stateStudentId, student.localStudentId];
    const match = findSisInRoster(candidateIds, roster);
    if (match) matchedSis.add(match);
    return toRow(student, match, thresholds, reportYear, statusFor(match));
  });
  for (const student of roster) {
    if (!matchedSis.has(student)) {
      rows.push(toRow(null, student, thresholds, reportYear, statusFor(student)));
    }
  }
  return rows;
}

export function sortRowsByName(rows: RosterRow[], direction: 'asc' | 'desc'): RosterRow[] {
  const sorted = [...rows].sort((a, b) => a.sortKey.localeCompare(b.sortKey));
  return direction === 'desc' ? sorted.reverse() : sorted;
}

export function filterRows(rows: RosterRow[], query: string): RosterRow[] {
  const q = query.trim().toLowerCase();
  if (q === '') return rows;
  return rows.filter(
    (row) => row.name.toLowerCase().includes(q) || row.id.toLowerCase().includes(q),
  );
}

export function filterRowsBySchool(rows: RosterRow[], school: string): RosterRow[] {
  const key = school.trim().toLowerCase();
  if (key === '') return rows;
  return rows.filter((row) => row.school.trim().toLowerCase() === key);
}

export function pageRows(rows: RosterRow[], page: number, perPage: number): RosterRow[] {
  return rows.slice((page - 1) * perPage, page * perPage);
}

export function rangeLabel(page: number, perPage: number, total: number): string {
  if (total === 0) return '0-0 of 0';
  const start = (page - 1) * perPage + 1;
  const end = Math.min(page * perPage, total);
  return `${start}-${end} of ${total}`;
}
